import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Stack, Typography } from '@mui/material';
import PaiAutoComplete, { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { CountryCode } from '../linkedin-accounts.types';
import { countryCodeToOptions, getCitiesForCountry } from '../utils/country-city-helpers';
import StepActions from './step-actions';
import StepHint from './step-hint';
import StepLayout from './step-layout';

interface Props {
  loading: boolean;

  allCountryCodes: CountryCode[];
  selectedCountry: OptionType;
  selectedCity: OptionType;

  handleCountryChange(event: React.ChangeEvent<any>, newValue: any): void;

  handleCityChange(event: React.ChangeEvent<any>, newValue: any): void;

  onBack(): void;

  /** Legt das Konto an und startet die Anmeldung — der Punkt, ab dem es automatisch läuft. */
  onStartLogin(): void;
}

/**
 * Schritt 3: der Standort, an dem das Konto online ist.
 *
 * Das war vorher ein „Proxy-Server"-Feld mitten im Zugangsdaten-Formular und klang wie
 * eine technische Einstellung. Es ist aber die Voraussetzung dafür, dass die Anmeldung
 * dauerhaft vertraut bleibt: rotierende Adressen erzwingen tägliche 2FA-Abfragen, eine
 * eigene feste IP nicht. Deshalb ein eigener Schritt mit dem Satz, der das erklärt.
 */
const SelectLocationStep: React.FC<Props> = ({
  loading,
  allCountryCodes,
  selectedCountry,
  selectedCity,
  handleCountryChange,
  handleCityChange,
  onBack,
  onStartLogin,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  // Feste IPs gibt es nur in Deutschland: die Plattform bestellt sie dort und hält einen
  // Vorrat bereit, damit die Anmeldung nicht auf die Freischaltung warten muss. Eine Stadt
  // wählen zu lassen hieße etwas zu versprechen, das der Kauf nicht hält — iProyal liefert
  // feste IPs nur in wenigen Städten, die meisten davon ausverkauft (Stuttgart, 06.10.2026).
  const countryCodeOptions = countryCodeToOptions(allCountryCodes).filter((c) => c.value === FIXED_IP_COUNTRY);
  const germany = countryCodeOptions[0];
  const germanCities = germany ? getCitiesForCountry(allCountryCodes, germany.value) : [];
  const defaultCity = germanCities.find((c) => c.value === DEFAULT_CITY) ?? germanCities[0];

  // Die Auswahl-Handler ignorieren ihr Event und lesen nur den neuen Wert.
  useEffect(() => {
    if (germany && selectedCountry?.value !== germany.value) {
      handleCountryChange({} as React.ChangeEvent<any>, germany);
    }
  }, [germany?.value, selectedCountry?.value]);

  useEffect(() => {
    if (selectedCountry?.value === FIXED_IP_COUNTRY && !selectedCity?.value && defaultCity) {
      handleCityChange({} as React.ChangeEvent<any>, defaultCity);
    }
  }, [selectedCountry?.value, selectedCity?.value, defaultCity?.value]);

  return (
    <StepLayout
      title={t('appSyncLocationTitle', 'Wo soll das Konto online sein?')}
      description={t(
        'appSyncLocationDescription',
        'Dein Konto bekommt eine eigene, feste IP-Adresse in Deutschland. Sie bleibt dauerhaft dieselbe — das hält die Anmeldung bei LinkedIn vertraut.',
      )}
      footer={
        <StepActions
          primaryText={t('appSyncStartLogin', 'Anmeldung starten')}
          primaryDisabled={!selectedCountry?.value || !selectedCity?.value}
          primaryLoading={loading}
          backText={t('back')}
          onBack={onBack}
          onPrimary={onStartLogin}
        />
      }
    >
      <Stack gap={2.25}>
        <Stack gap={0.75}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Typography className={classes.fieldLabel}>{t('appSyncCountry', 'Land')}</Typography>
            <PaiHelpIcon tooltipText={t('proxyServerInformationDescription')} />
          </Stack>
          <PaiAutoComplete
            options={countryCodeOptions}
            selected={selectedCountry || { label: '', value: '' }}
            placeholder={t('appSyncCountry', 'Land')}
            disableClearable={true}
            disabled={true}
            onChange={handleCountryChange}
          />
          <Typography className={classes.helperText}>
            {t('appSyncCountryGermanyOnly', 'Feste IPs stellen wir derzeit in Deutschland bereit.')}
          </Typography>
        </Stack>

        <StepHint
          icon={<LocationOnIcon />}
          text={t(
            'appSyncLocationHint',
            'Eine eigene, feste IP ist Voraussetzung für den App-Login — sie verhindert die täglichen 2FA-Abfragen, die bei wechselnden Adressen entstehen. Nur für dieses Konto, wird von uns beschafft.',
          )}
        />
      </Stack>
    </StepLayout>
  );
};

const FIXED_IP_COUNTRY = 'de';
/** Nur ein Platzhalter für das Pflichtfeld — die feste IP hängt nicht an der Stadt. */
const DEFAULT_CITY = 'berlin';

const useStyles = makeStyles()((theme) => ({
  fieldLabel: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    color: theme.palette.greys.medium,
  },
  helperText: {
    fontSize: theme.typography.pxToRem(12.5),
    color: theme.palette.greys.medium,
  },
}));

export default SelectLocationStep;
